class CartRemoveButton extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('click', (event) => {
      event.preventDefault();
      const cartItems = this.closest('cart-items') || this.closest('cart-drawer-items');
      cartItems.updateQuantity(this.dataset.index, 0);
    });
  }
}

customElements.define('cart-remove-button', CartRemoveButton);

class CartItems extends HTMLElement {
  constructor() {
    super();
    this.lineItemStatusElement =
      document.getElementById('shopping-cart-line-item-status') || document.getElementById('CartDrawer-LineItemStatus');

    const debouncedOnChange = debounce((event) => {
      this.onChange(event);
    }, ON_CHANGE_DEBOUNCE_TIMER);

    this.addEventListener('change', debouncedOnChange.bind(this));
  }

  cartUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-items') {
        return;
      }
      this.onCartUpdate();
    });
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
  }

  onChange(event) {
    this.updateQuantity(
      event.target.dataset.index,
      event.target.value,
      document.activeElement.getAttribute('name'),
      event.target.dataset.quantityVariantId
    );
  }

  onCartUpdate() {
    if (this.tagName === 'CART-DRAWER-ITEMS') {
      // Store checkbox state before DOM replacement
      const checkboxState = this.preserveCheckboxState();
      
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const selectors = ['cart-drawer-items', '.cart-drawer__footer', '#cart-discount-50-off-container'];
          for (const selector of selectors) {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          }
          
          // Restore checkbox state after DOM replacement
          this.restoreCheckboxState(checkboxState);

          // After DOM replacement, recompute visible items and toggle empty state
          this.updateEmptyStateFromCart();

          // Notify listeners that cart DOM has been updated
          const cartUpdatedEvent = new CustomEvent('cartUpdated', {
            detail: { message: 'Cart DOM updated (drawer)' }
          });
          document.dispatchEvent(cartUpdatedEvent);

          setTimeout(() => {
            if (window.cartCheckboxUpsellInstance) {
              window.cartCheckboxUpsellInstance.updateCheckboxState();
            }
          }, 100);
        })
        .catch((e) => {
          console.error(e);
        });
    } else {
      fetch(`${routes.cart_url}?section_id=main-cart-items`)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          const sourceQty = html.querySelector('cart-items');
          this.innerHTML = sourceQty.innerHTML;

          // After DOM replacement, recompute visible items and toggle empty state
          this.updateEmptyStateFromCart();

          // Notify listeners that cart DOM has been updated
          const cartUpdatedEvent = new CustomEvent('cartUpdated', {
            detail: { message: 'Cart DOM updated (page)' }
          });
          document.dispatchEvent(cartUpdatedEvent);

          setTimeout(() => {
            if (window.cartCheckboxUpsellInstance) {
              window.cartCheckboxUpsellInstance.updateCheckboxState();
            }
          }, 100);
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  // Helper: determine number of visible (non fast-processing upsell and non-hidden upsell) items
  getVisibleItemsCount(items) {
    if (!items) return 0;
    const fastProcessingEnabled = window.theme?.settings?.cart_upsell_fast_processing;
    return items.filter((item) => {
      // Hide fast processing items
      if (fastProcessingEnabled) {
        const fp = item.properties && item.properties._fast_processing;
        if (fp === true || fp === 'true' || (typeof fp !== 'undefined' && String(fp).toLowerCase() === 'true')) {
          return false;
        }
      }
      
      // Hide bound upsell items
      const isHiddenUpsell = item.properties && item.properties._is_hidden_upsell === "true";
      if (isHiddenUpsell) {
        return false;
      }
      
      return true;
    }).length;
  }

  // Fetch cart JSON and update empty state classes based on visible items
  updateEmptyStateFromCart() {
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const visibleCount = this.getVisibleItemsCount(cart.items || []);
        const isDisplayEmpty = visibleCount === 0;
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        const cartItemsEl = document.querySelector('cart-items');
        const cartDrawerItemsEl = document.querySelector('cart-drawer-items');
        [cartItemsEl, cartDrawerItemsEl, cartDrawerWrapper, cartFooter].forEach(el => {
          if (el) el.classList.toggle('is-empty', isDisplayEmpty);
        });

        // If no visible items remain, ensure backend cart is actually empty (remove fast-processing items) then render empty UI
        if (isDisplayEmpty) {
          const fastProcessingItems = (cart.items || []).filter(item => {
            const fp = item.properties && item.properties._fast_processing;
            return fp === true || fp === 'true' || (typeof fp !== 'undefined' && String(fp).toLowerCase() === 'true');
          });
          // If cart still contains only fast-processing items, remove them to make cart truly empty
            if (fastProcessingItems.length === cart.items.length && cart.items.length > 0) {
              const updates = {};
              fastProcessingItems.forEach(i => { updates[i.key] = 0; });
              fetch('/cart/update.js', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ updates })
              }).then(() => {
                this.fetchAndRenderEmptyState();
              }).catch(() => {
                this.fetchAndRenderEmptyState();
              });
              return;
            }
          // If backend already empty OR we just removed items previously, render empty state if needed
          if (cart.item_count === 0 || fastProcessingItems.length === cart.items.length) {
            this.fetchAndRenderEmptyState();
          }
        }
      })
      .catch(() => { /* swallow */ });
  }

  fetchAndRenderEmptyState() {
    if (this._renderingEmpty) return; // guard
    this._renderingEmpty = true;
    const isDrawer = !!document.querySelector('cart-drawer');
    if (isDrawer) {
      fetch(`${routes.cart_url}?section_id=cart-drawer`)
        .then(r => r.text())
        .then(htmlText => {
          const html = new DOMParser().parseFromString(htmlText, 'text/html');
          const newInner = html.querySelector('#CartDrawer .drawer__inner');
          const drawer = document.querySelector('cart-drawer');
          const currentInner = drawer && drawer.querySelector('.drawer__inner');
          if (newInner && currentInner) {
            currentInner.replaceWith(newInner);
          }
          if (drawer) drawer.classList.add('is-empty');
        })
        .finally(() => { this._renderingEmpty = false; });
    } else {
      // Main cart page
      Promise.all([
        fetch(`${routes.cart_url}?section_id=main-cart-items`).then(r => r.text()),
        fetch(`${routes.cart_url}?section_id=main-cart-footer`).then(r => r.text()).catch(() => null)
      ]).then(([itemsHtml, footerHtml]) => {
        const parser = new DOMParser();
        const itemsDoc = parser.parseFromString(itemsHtml, 'text/html');
        const newItems = itemsDoc.querySelector('cart-items');
        if (newItems) {
          const current = document.querySelector('cart-items');
            if (current) current.innerHTML = newItems.innerHTML; // keeps same element to preserve listeners
        }
        if (footerHtml) {
          const footerDoc = parser.parseFromString(footerHtml, 'text/html');
          const footerSection = footerDoc.querySelector('#main-cart-footer .js-contents');
          const currentFooter = document.querySelector('#main-cart-footer .js-contents');
          if (footerSection && currentFooter) currentFooter.innerHTML = footerSection.innerHTML;
        }
        const cartItemsEl = document.querySelector('cart-items');
        if (cartItemsEl) cartItemsEl.classList.add('is-empty');
        const footer = document.getElementById('main-cart-footer');
        if (footer) footer.classList.add('is-empty');
      }).finally(() => { this._renderingEmpty = false; });
    }
  }

  updateQuantity(line, quantity, name, variantId) {
    this.enableLoading(line);

    // Check if we're removing a main product and need to remove bound upsell items
    if (quantity === 0) {
      this.handleBoundItemRemoval(line);
    }

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.getSectionsToRender().map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);
        const quantityElement =
          document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
        
        if (!quantityElement) {
          return;
        }

        const items = document.querySelectorAll('.cart-item');

        if (parsedState.errors) {
          quantityElement.value = quantityElement.getAttribute('value');
          this.updateLiveRegions(line, parsedState.errors);
          return;
        }

        const visibleItemsCount = this.getVisibleItemsCount(parsedState.items);
        // If only fast-processing items remain, remove them then render empty and stop further processing
        if (visibleItemsCount === 0 && parsedState.items && parsedState.items.length > 0) {
          const fastProcessingItems = parsedState.items.filter(item => {
            const fp = item.properties && item.properties._fast_processing;
            return fp === true || fp === 'true' || (typeof fp !== 'undefined' && String(fp).toLowerCase() === 'true');
          });
          if (fastProcessingItems.length === parsedState.items.length) {
            const updates = {};
            fastProcessingItems.forEach(i => { updates[i.key] = 0; });
            fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ updates })
            }).then(() => {
              this.fetchAndRenderEmptyState();
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: { item_count: 0 }, variantId: variantId });
            }).catch(() => {
              this.fetchAndRenderEmptyState();
            });
            return; // prevent normal rendering path
          }
        }

        const isDisplayEmpty = visibleItemsCount === 0;
        this.classList.toggle('is-empty', isDisplayEmpty);
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const cartFooter = document.getElementById('main-cart-footer');
        if (cartFooter) cartFooter.classList.toggle('is-empty', isDisplayEmpty);
        if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', isDisplayEmpty);

        this.getSectionsToRender().forEach((section) => {
          
          const elementToReplace =
            document.getElementById(section.id).querySelector(section.selector) || document.getElementById(section.id);
          elementToReplace.innerHTML = this.getSectionInnerHTML(
            parsedState.sections[section.section],
            section.selector
          );
        });
        // After sections replacement, ensure empty state is correct (in case markup conditions differ)
        this.updateEmptyStateFromCart();
        const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
        let message = '';
        if (items.length === parsedState.items.length && updatedValue !== parseInt(quantityElement.value)) {
          if (typeof updatedValue === 'undefined') {
            message = window.cartStrings.error;
          } else {
            message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }
        }
        this.updateLiveRegions(line, message);

        const lineItem =
          document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
        if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
          cartDrawerWrapper
            ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
            : lineItem.querySelector(`[name="${name}"]`).focus();
        } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
        } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
          trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
        }

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
        
        // Dispatch cartUpdated event after successful update
        const cartUpdatedEvent = new CustomEvent('cartUpdated', {
          detail: {
            message: 'Cart was updated',
            cartData: parsedState
          }
        });
        document.dispatchEvent(cartUpdatedEvent);
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.disableLoading(line);
      });
      
  }

  handleBoundItemRemoval(line) {
    // Get current cart to find the item being removed and any bound items
    fetch('/cart.js')
      .then(r => r.json())
      .then(cart => {
        const itemBeingRemoved = cart.items[line - 1];
        if (!itemBeingRemoved) return;

        // Find all items that are bound to this main product
        const boundItems = cart.items.filter(item => {
          const boundToVariant = item.properties && item.properties._upsell_of;
          const isHiddenUpsell = item.properties && item.properties._is_hidden_upsell === "true";
          return boundToVariant === String(itemBeingRemoved.variant_id) && isHiddenUpsell;
        });

        if (boundItems.length > 0) {
          // Remove all bound items
          const updates = {};
          boundItems.forEach(item => {
            updates[item.key] = 0;
          });

          fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ updates })
          }).catch(err => {
            console.warn('Failed to remove bound upsell items:', err);
          });
        }
      })
      .catch(err => {
        console.warn('Failed to check for bound items:', err);
      });
  }

  updateLiveRegions(line, message) {
    const lineItemError =
      document.getElementById(`Line-item-error-${line}`) || document.getElementById(`CartDrawer-LineItemError-${line}`);
    if (lineItemError) lineItemError.querySelector('.cart-item__error-text').innerHTML = message;

    this.lineItemStatusElement.setAttribute('aria-hidden', true);

    const cartStatus =
      document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
    cartStatus.setAttribute('aria-hidden', false);

    setTimeout(() => {
      cartStatus.setAttribute('aria-hidden', true);
    }, 1000);
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  enableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.add('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    [...cartItemElements, ...cartDrawerItemElements].forEach((overlay) => overlay.classList.remove('hidden'));

    document.activeElement.blur();
    this.lineItemStatusElement.setAttribute('aria-hidden', false);
  }

  disableLoading(line) {
    const mainCartItems = document.getElementById('main-cart-items') || document.getElementById('CartDrawer-CartItems');
    mainCartItems.classList.remove('cart__items--disabled');

    const cartItemElements = this.querySelectorAll(`#CartItem-${line} .loading__spinner`);
    const cartDrawerItemElements = this.querySelectorAll(`#CartDrawer-Item-${line} .loading__spinner`);

    cartItemElements.forEach((overlay) => overlay.classList.add('hidden'));
    cartDrawerItemElements.forEach((overlay) => overlay.classList.add('hidden'));
  }

  preserveCheckboxState() {
    const checkbox = document.querySelector('#cart-upsell-checkbox');
    return {
      exists: !!checkbox,
      checked: checkbox ? checkbox.checked : false,
      variantId: checkbox ? checkbox.getAttribute('data-variant-id') : null
    };
  }

  restoreCheckboxState(checkboxState) {
    // Wait for new checkbox to be available in DOM
    setTimeout(() => {
      const newCheckbox = document.querySelector('#cart-upsell-checkbox');
      if (newCheckbox) {
        if (checkboxState.exists && checkboxState.variantId === newCheckbox.getAttribute('data-variant-id')) {
          // Restore previous state if it existed
          if (newCheckbox.checked !== checkboxState.checked) {
            newCheckbox.checked = checkboxState.checked;
          }
        } else if (!checkboxState.exists) {
          // If no previous state, ensure checkbox defaults to checked (as set in HTML)
          // This preserves the default checked state from the Liquid template
          if (!newCheckbox.checked) {
            newCheckbox.checked = true;
          }
        }
        
        // Only re-initialize if there's no active instance
        if (typeof initializeCartCheckboxUpsell === 'function' && !window.cartCheckboxUpsellInstance) {
          initializeCartCheckboxUpsell();
        }
      }
    }, 100);
  }
}

customElements.define('cart-items', CartItems);

if (!customElements.get('cart-note')) {
  customElements.define(
    'cart-note',
    class CartNote extends HTMLElement {
      constructor() {
        super();

        this.addEventListener(
          'input',
          debounce((event) => {
            const body = JSON.stringify({ note: event.target.value });
            fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } });
          }, ON_CHANGE_DEBOUNCE_TIMER)
        );
      }
    }
  );
}
